/**
 * 工具函数模块
 * @module utils/index
 */

import { v4 as uuidv4 } from 'uuid';

export const DEFAULT_PROJECT_VERSION = '1.0.0';
export const MQTT_RUNTIME_PASSWORD_PLACEHOLDER = '运行时输入';

const XML_ENTITY_DECODE_MAP: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&#34;': '"',
  '&#39;': "'",
  '&#60;': '<',
  '&#62;': '>',
  '&#x22;': '"',
  '&#x27;': "'",
  '&#x3c;': '<',
  '&#x3e;': '>',
  '&amp;': '&',
};

export function isRuntimeMqttPasswordPlaceholder(value: unknown): boolean {
  return String(value ?? '').trim() === MQTT_RUNTIME_PASSWORD_PLACEHOLDER;
}

export function isRuntimeMqttPasswordCodeLiteral(value: string): boolean {
  const normalized = String(value || '').trim();
  return normalized === JSON.stringify(MQTT_RUNTIME_PASSWORD_PLACEHOLDER)
    || normalized === `'${MQTT_RUNTIME_PASSWORD_PLACEHOLDER.replace(/'/g, "\\'")}'`;
}

function decodeXmlEntities(value: string): string {
  return value.replace(/&(lt|gt|quot|apos|amp|#34|#39|#60|#62|#x22|#x27|#x3c|#x3e);/gi, (entity) => {
    const decoded = XML_ENTITY_DECODE_MAP[entity.toLowerCase()];
    return decoded ?? entity;
  });
}

function unwrapQuotedText(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length < 2) {
    return trimmed;
  }

  const firstChar = trimmed[0];
  const lastChar = trimmed[trimmed.length - 1];
  if ((firstChar !== '"' && firstChar !== "'") || firstChar !== lastChar) {
    return trimmed;
  }

  const inner = trimmed.slice(1, -1).trim();
  if (!inner) {
    return inner;
  }

  return /(<\/?(?:xml|block)\b|\\["'nrt]|&lt;|&#60;|&#x3c;)/i.test(inner) ? inner : trimmed;
}

export function normalizePossiblyEscapedXml(value: string): string {
  let normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }

  for (let attempt = 0; attempt < 3; attempt += 1) {
    let next = unwrapQuotedText(normalized);

    if (/\\(?:r\\n|n|r|t|"|'|\\|u003[cCeE]|u002[267]|x3[cCeE]|x2[267])/i.test(next)) {
      next = next
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\u003[cC]/g, '<')
        .replace(/\\u003[eE]/g, '>')
        .replace(/\\u0022/g, '"')
        .replace(/\\u0027/g, "'")
        .replace(/\\u0026/g, '&')
        .replace(/\\x3[cC]/g, '<')
        .replace(/\\x3[eE]/g, '>')
        .replace(/\\x22/g, '"')
        .replace(/\\x27/g, "'")
        .replace(/\\x26/g, '&')
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'")
        .replace(/\\\\/g, '\\');
    }

    if (/&(lt|gt|quot|apos|amp|#34|#39|#60|#62|#x22|#x27|#x3c|#x3e);/i.test(next)) {
      next = decodeXmlEntities(next);
    }

    next = next.trim();
    if (next === normalized) {
      break;
    }

    normalized = next;
  }

  return normalized;
}

export function parseSemver(version: string): [number, number, number] | null {
  const normalized = String(version || '').trim();
  const match = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/.exec(normalized);
  if (!match) {
    return null;
  }

  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

export function isValidSemver(version: string): boolean {
  return parseSemver(version) !== null;
}

export function compareSemver(left: string, right: string): number | null {
  const leftParts = parseSemver(left);
  const rightParts = parseSemver(right);
  if (!leftParts || !rightParts) {
    return null;
  }

  for (let index = 0; index < leftParts.length; index += 1) {
    if (leftParts[index] > rightParts[index]) {
      return 1;
    }
    if (leftParts[index] < rightParts[index]) {
      return -1;
    }
  }

  return 0;
}

export function bumpPatchSemver(version: string): string {
  const parsed = parseSemver(version);
  if (!parsed) {
    return DEFAULT_PROJECT_VERSION;
  }

  return `${parsed[0]}.${parsed[1]}.${parsed[2] + 1}`;
}

export function resolveProjectThumbnailSrc(value: string | null | undefined): string | null {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return null;
  }

  if (/^data:\s*,\s*$/i.test(normalized)) {
    return null;
  }

  const normalizedPath = normalized.replace(/\\+/g, '/');

  if (
    normalizedPath.startsWith('data:')
    || normalizedPath.startsWith('blob:')
    || normalizedPath.startsWith('http://')
    || normalizedPath.startsWith('https://')
    || normalizedPath.startsWith('/')) {
    return normalizedPath;
  }

  if (normalizedPath.startsWith('public/')) {
    return `/${normalizedPath.slice('public/'.length)}`;
  }

  if (normalizedPath.startsWith('./')) {
    return `/${normalizedPath.slice(2)}`;
  }

  return `/${normalizedPath.replace(/^\/+/, '')}`;
}

export function shouldGenerateProjectThumbnail(value: string | null | undefined): boolean {
  return resolveProjectThumbnailSrc(value) === null;
}

/**
 * 生成唯一ID
 */
export function generateId(): string {
  return uuidv4();
}

/**
 * 防抖函数
 * @param fn 要执行的函数
 * @param delay 延迟时间（毫秒）
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return function (this: unknown, ...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

/**
 * 节流函数
 * @param fn 要执行的函数
 * @param limit 时间限制（毫秒）
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  
  return function (this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * 格式化日期时间
 * @param date 日期对象或字符串
 * @param format 格式字符串
 */
export function formatDateTime(date: Date | string, format = 'YYYY-MM-DD HH:mm:ss'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  const pad = (n: number) => n.toString().padStart(2, '0');
  
  const replacements: Record<string, string> = {
    'YYYY': d.getFullYear().toString(),
    'MM': pad(d.getMonth() + 1),
    'DD': pad(d.getDate()),
    'HH': pad(d.getHours()),
    'mm': pad(d.getMinutes()),
    'ss': pad(d.getSeconds()),
  };
  
  let result = format;
  for (const [key, value] of Object.entries(replacements)) {
    result = result.replace(key, value);
  }
  
  return result;
}

/**
 * 净化文件名，移除非法字符
 * @param filename 原始文件名
 */
export function sanitizeFilename(filename: string): string {
  // 移除Windows和Unix下的非法字符
  const sanitized = filename.replace(/[<>:"/\\|?*\x00-\x1f]/g, '');
  return sanitized.trim() || 'project';
}

/**
 * 下载文件
 * @param content 文件内容
 * @param filename 文件名
 * @param mimeType MIME类型
 */
export async function downloadFile(content: string | Blob, filename: string, mimeType = 'text/plain'): Promise<boolean> {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });

  const pickerWindow = window as Window & {
    showSaveFilePicker?: (options?: Record<string, unknown>) => Promise<FileSystemFileHandle>;
  };
  if (typeof pickerWindow.showSaveFilePicker === 'function') {
    try {
      const extensionMatch = filename.match(/(\.[^.]+)$/);
      const extension = extensionMatch?.[1];
      const handle = await pickerWindow.showSaveFilePicker({
        suggestedName: filename,
        ...(extension
          ? {
              types: [
                {
                  description: `${extension.slice(1).toUpperCase()} 文件`,
                  accept: { [mimeType]: [extension] },
                },
              ],
            }
          : {}),
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return false;
      }
    }
  }

  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);

  return true;
}

/**
 * 复制文本到剪贴板
 * @param text 要复制的文本
 * @returns 是否成功
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // 降级方案
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

/**
 * 安全解析JSON
 * @param jsonString JSON字符串
 * @param fallback 解析失败时的默认值
 */
export function safeJsonParse<T>(jsonString: string, fallback: T): T {
  try {
    return JSON.parse(jsonString) as T;
  } catch {
    return fallback;
  }
}

/**
 * 深拷贝对象
 * @param obj 要拷贝的对象
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * 延迟执行
 * @param ms 毫秒数
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * 获取当前ISO时间字符串
 */
export function nowISO(): string {
  return new Date().toISOString();
}
